"use client";

import React from "react";
import { Client } from "@/lib/types/database";
import { useGoogleDriveStatus } from "@/lib/hooks/google-drive/use-google-drive-status";
import { useGoogleDriveBreadcrumbs } from "@/lib/hooks/google-drive/use-google-drive-breadcrumbs";
import { useCompany } from "@/features/companies/hooks/companies-base-queries";
import { GoogleDriveBrowser } from "@/components/crm/drive/GoogleDriveBrowser";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  LoadingSpinner,
  EmptyState,
} from "@west-tennessee-consulting/ui";
import { HardDrive } from "lucide-react";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";

interface ClientDriveTabProps {
  client: Client;
}

export const ClientDriveTab: React.FC<ClientDriveTabProps> = ({ client }) => {
  const { data: status, isLoading: statusLoading } = useGoogleDriveStatus();
  const { session } = useUniversalAuth();

  const hasAnyDriveAccess =
    !!status?.primary?.hasDriveAccess || !!status?.secondary?.hasDriveAccess;

  // The Drive folder is company-level. Resolve the contact's company by id and
  // read its stored google_drive_folder_id — never name-match.
  const companyId = client.company_id || undefined;
  const { data: company, isLoading: companyLoading } = useCompany(companyId, {
    enabled: hasAnyDriveAccess && !!companyId,
  });

  const storedFolderId = company?.google_drive_folder_id?.trim() || undefined;

  // Breadcrumbs computed off the stored folder id only (hook called
  // unconditionally to keep hook order stable; no-op when there is no id).
  const { breadcrumbs: reusableBreadcrumbs, parentName: reusableParentName } =
    useGoogleDriveBreadcrumbs({
      enabled: hasAnyDriveAccess && !!storedFolderId,
      targetFolderId: storedFolderId,
    });

  // Single return to keep hooks count consistent across renders
  return (
    <>
      {!statusLoading && (!status || !hasAnyDriveAccess) ? (
        <Card>
          <CardHeader>
            <CardTitle>Google Drive not configured</CardTitle>
            <CardDescription>Connect your Google account to access customer files.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <a href="/crm/calendar/setup">Go to Drive setup</a>
            </Button>
          </CardContent>
        </Card>
      ) : statusLoading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <LoadingSpinner className="h-5 w-5" />
          Checking Drive status...
        </div>
      ) : companyId && companyLoading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <LoadingSpinner className="h-5 w-5" />
          Loading customer files...
        </div>
      ) : !storedFolderId ? (
        <EmptyState
          icon={HardDrive}
          title="Drive folder not provisioned yet"
          description="This contact's company doesn't have a Google Drive folder linked yet. It will appear here automatically once the folder has been provisioned — nothing to do in the meantime."
        />
      ) : (
        <GoogleDriveBrowser
          showHeader={true}
          maxHeight="600px"
          initialParentId={storedFolderId}
          initialParentName={reusableParentName || company?.name || "Folder"}
          initialBreadcrumbs={reusableBreadcrumbs}
          breadcrumbLoading={reusableBreadcrumbs.length === 0}
          compactMode={false}
          allowDownload={true}
          allowView={true}
          allowCreateFolder={true}
          allowUpload={true}
          className="mt-2"
        />
      )}
    </>
  );
};
